import { useState, useEffect, useCallback } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import PartyPopperIcon from '@atlaskit/icon-lab/core/party-popper';
import { Text, Flex, Inline, xcss } from '@atlaskit/primitives';

import { useAppData } from 'src/app/data';
import { fetchData } from 'src/components/pipelines/resources/utils';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { useConfigBoolean } from 'src/contexts/statsig-config-provider';
import { StatsigFeatureKeys } from 'src/features/features';
import { useParams } from 'src/router/hooks';
import { atlassianStore as store } from 'src/utils/store';

/*
  Temporary marketing banner shown for Pipelines Growth hack.

  Logic in useMarketingBanner hook, requires:
  Dynamic config FF - for killswitch
  enabled from API - for checking eligible repos
  must have a repoSlug - for ensuring only display within repository pages
  banner hasn't been dismissed - tracked in local state and also in localstorage tracked per user per repository.

  Component will be removed with the FF:

  https://switcheroo.atlassian.com/ui/gates/54509725-c2f0-42b2-91f3-cd4cc81a51b2/key/bitbucket_pipelines_free-minutes-banner-enabled
*/

export const marketingStorageKey = (
  feature: string,
  userId: string,
  repoSlug: string
): string => `marketing::${feature}-${userId}-${repoSlug}`;

export function getMarketingBannerDismissed(
  feature: string,
  userId: string,
  repoSlug: string
): boolean {
  return store.get(marketingStorageKey(feature, userId, repoSlug), false);
}

export function setMarketingBannerDismissed(
  feature: string,
  userId: string,
  repoSlug: string
): void {
  store.setStrictlyNecessaryItem(
    marketingStorageKey(feature, userId, repoSlug),
    true
  );
}

export const useMarketingBanner = (): {
  shouldDisplayMarketingBanner: boolean;
  dismissMarketingBanner: () => void;
} => {
  const featureGate = StatsigFeatureKeys.isPipelinesFreeMinutesEnabled;
  const isPipelinesFreeMinutesEnabled = useConfigBoolean(featureGate, false);

  const { repositorySlug, repositoryFullSlug, repositoryOwner } = useParams<{
    repositorySlug?: string;
    repositoryFullSlug?: string;
    repositoryOwner?: string;
  }>();
  const repoSlug = repositorySlug || repositoryFullSlug?.split('/')[1];
  const repoFullSlug =
    repositoryFullSlug || `${repositoryOwner}/${repositorySlug}`;

  const { user } = useAppData();

  const [enabledForRepo, setEnabledForRepo] = useState(false);
  const [display, setDisplay] = useState(
    () =>
      !getMarketingBannerDismissed(
        featureGate,
        user?.uuid ?? '',
        repoSlug ?? ''
      )
  );

  const fetchRepoEnabled = useCallback(async () => {
    const { enabled } = await fetchData(
      `${envBaseUrl(
        '/!api/internal'
      )}/repositories/${repoFullSlug}/pipelines-config/free-minutes-enabled`,
      'pipelines-free-minutes-campaign-exception'
    );

    if (enabled) setEnabledForRepo(true);
  }, [repoFullSlug]);

  useEffect(() => {
    if (!isPipelinesFreeMinutesEnabled) return;
    // do not fetch if repo isn't available
    if (!repoSlug) return;

    fetchRepoEnabled();
  }, [fetchRepoEnabled, isPipelinesFreeMinutesEnabled, repoSlug]);

  return {
    shouldDisplayMarketingBanner: !!(
      isPipelinesFreeMinutesEnabled &&
      enabledForRepo &&
      repoSlug &&
      user &&
      display
    ),
    dismissMarketingBanner: () => {
      setDisplay(false);
      setMarketingBannerDismissed(
        featureGate,
        user?.uuid ?? '',
        repoSlug ?? ''
      );
    },
  };
};

const messages = defineMessages({
  title: {
    id: 'frontbucket.marketingBanner.marchMadness.title',
    description:
      'Title of banner shown at top of screen to promote March madness growth hack offer',
    defaultMessage:
      'March Madness: 5K free build minutes on this repository until April 12th.',
  },
  description: {
    id: 'frontbucket.marketingBanner.marchMadness.description',
    description:
      'Description of banner shown at top of screen to promote March madness growth hack offer',
    defaultMessage:
      '{title} Experience native CI/CD that lives where your code lives.',
  },
  dismiss: {
    id: 'frontbucket.marketingBanner.marchMadness.dismiss',
    description:
      'Dismiss CTA of banner shown at top of screen to promote March madness growth hack offer',
    defaultMessage: 'Dismiss',
  },
});

const containerStyles = xcss({
  backgroundColor: 'color.background.neutral.bold',
  height: '48px',
  color: 'color.text.inverse',
  paddingInline: 'space.200',
});

const textContainerStyles = xcss({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexShrink: 0,
});

export const MarketingBanner = ({ onDismiss }: { onDismiss: () => void }) => {
  return (
    <Flex
      xcss={containerStyles}
      justifyContent="space-between"
      alignItems="center"
    >
      <Inline xcss={textContainerStyles} space="space.150" alignInline="center">
        <PartyPopperIcon label="" />

        <Text color="color.text.inverse">
          <FormattedMessage
            {...messages.description}
            values={{
              title: (
                <Text weight="bold" color="color.text.inverse">
                  <FormattedMessage {...messages.title} />
                </Text>
              ),
            }}
          />
        </Text>
      </Inline>
      <Button appearance="subtle" onClick={onDismiss}>
        <Text color="color.text.inverse">
          <FormattedMessage {...messages.dismiss} />
        </Text>
      </Button>
    </Flex>
  );
};
